import Phaser from 'phaser';
import {
  ClientMessage,
  costumeTint,
  ServerMessage,
  type ChatPostedPayload,
  type EmotePostedPayload,
  type ErrorPayload,
  type GameMode,
  type GameStartingPayload,
} from '@ponponland/shared';
import type { Room } from 'colyseus.js';
import { escapeHtml, mountRoot, showToast } from '../ui/dom.js';
import { ponBlobHtml } from '../world/character/ponBlob.js';
import { Platformer } from '../world/movement.js';
import { CSS_CLASS_FOR, ponStateFrom } from '../world/character/ponState.js';
import {
  showOnScreenControls,
  mountTouchControls,
  type TouchControlsHandle,
} from '../world/touchControls.js';
import { mountEmoteWheel, type EmoteWheelHandle } from '../world/emoteWheel.js';
import { session } from '../state/session.js';
import { forgetRoom } from '../state/persist.js';
import { leaveGame, net } from '../net/client.js';
import type { TilesetId } from '../config/tilesets.js';
import { PLAZA_PON_SIZE } from '../world/scale.js';
import { backdropUrl } from '../world/backdrop.js';
import { DOM_TILE, dirtFillUrl, grassCapUrl } from '../world/terrain.js';
import { CRATE_FILE, propUrl } from '../world/props.js';
import type { PlayerView, RoomSchema } from '../net/roomSchema.js';

// Plaza coordinate space. The VIEW is the fixed 880x430 `.pp-root` viewport; the
// WORLD is a wider scrollable map the camera pans across (Unity-style follow). The
// local Pon's feet ride `PLAZA_GROUND_Y`; it runs/jumps clamped to the WORLD
// `[PLAZA_MIN_X, PLAZA_MAX_X]`, and the camera scrolls to keep it on screen.
const PLAZA_VIEW_W = 880;
const PLAZA_H = 430;
const PLAZA_WORLD_W = 1440; // scrollable map width (bigger than the frame, ~1.6 screens)
const PLAZA_TILESET: TilesetId = 'sunnyland';
const PLAZA_GROUND_Y = PLAZA_H - 118;
const PLAZA_MIN_X = 30;
const PLAZA_MAX_X = PLAZA_WORLD_W - 30;
// The plaza Pon size is the single on-screen-scale source (`config/worldScale`),
// shared with the stage so the in-game Pon renders at the EXACT plaza size.
// Camera dead-zone (Cinemachine-style): the view scrolls only once the player
// crosses these screen-x bounds, so they can roam the middle freely.
const PLAZA_CAM_L = PLAZA_VIEW_W * 0.36;
const PLAZA_CAM_R = PLAZA_VIEW_W * 0.64;
// Parallax factor for the far background vs the 1:1 world scroll (0 = fixed).
const PLAZA_BG_PARALLAX = 0.45;

// The "mountain": connected, SOLID, grass-capped earth blocks forming a terraced
// staircase (up to a summit, then a step down). Adjacent blocks share an edge
// (`left + w` === next `left`) so they connect with NO overlap and NO gap; each is
// solid (blocks sides + underside) and fills with tiles down to the ground.
// Steps rise by a gentle Δ40 (well under the ~80px hop) so the Pon can jump BACK
// UP the mountain reliably, not only down — more, smaller, narrower steps reach a
// taller summit.
type PlazaBlock = { left: number; w: number; top: number };
const PLAZA_BLOCKS: PlazaBlock[] = [
  { left: 480, w: 95, top: 272 }, // step 1 (Δ40 above the ground)
  { left: 575, w: 95, top: 232 }, // step 2
  { left: 670, w: 95, top: 192 }, // step 3
  { left: 765, w: 95, top: 152 }, // step 4
  { left: 860, w: 100, top: 112 }, // summit (200px up, narrow)
  { left: 960, w: 95, top: 200 }, // step down
];

// Floating one-way platforms (jump UP through from below, land on top) drifting
// in the sky — reachable by the Pon's ~80px hop. `x` = centre, `y` = top surface.
type PlazaFloat = { x: number; y: number; w: number };
const PLAZA_FLOATS: PlazaFloat[] = [
  { x: 220, y: 248, w: 92 }, // left — hop up from the ground
  { x: 330, y: 214, w: 100 }, // left-upper — hop from the one at 220
  { x: 195, y: 158, w: 92 }, // higher-left — hop up-across from the one at 330
  { x: 1085, y: 250, w: 92 }, // right — hop up from the ground
  { x: 1205, y: 206, w: 84 }, // higher — short hop across from the one at 1085
];

// Solid, pushable crates on the ground. The Pon collides with them (can't pass
// through) and shoves them slowly; the Absorb skill SUCKS one onto itself and
// carries it (which slows the Pon) until released. `x` = centre (mutable).
const CRATE_SIZE = 40;
const PLAZA_CRATE_START_X = [280, 1180, 1340]; // on the flat ground, clear of the blocks
const PLAZA_CRATE_BASE_Y = PLAZA_GROUND_Y + 8; // where a grounded crate's bottom rests
const PLAZA_PON_HALF = 16; // Pon body half-width for crate collision
const PLAZA_PUSH_SPEED = 1.25; // px/frame a shoved crate slides (slower than the 2.8 run)
const PLAZA_CARRY_SLOW = 0.55; // movement multiplier while carrying a sucked crate
const PLAZA_ABSORB_RANGE = 52; // px from the Pon a crate must be to get sucked
// Extra px the Pons sink below the sim floor so their feet sit IN the grass
// blades (the 2.png grass cap reads a bit below the tile's top edge), not
// hovering on the tile's top line. Applied to local + static roster alike.
const PLAZA_FOOT_SINK = 13;

/**
 * Lobby — port of the prototype `LobbyScene` (`docs/design/flow/screens2.jsx`)
 * to a server-synced scene. The required slice-2 surface (design spec §D /
 * "Lobby fidelity") is the **room-state surface**: roster with costumes + host
 * marker + ready ticks, ready/unready, host-only mode toggle + gated Start,
 * chat, and Leave. The interactive run/jump plaza is nice-to-have polish (design
 * spec) and is rendered as a cozy static plaza — the avatars stand in the
 * plaza, the door glows when everyone is ready.
 *
 * All room state is authoritative (the synced `RoomSchema`); the scene renders
 * it and `send(...)`s requests. It never decides outcomes (MASTER_PROMPT §4.3).
 * Section-1/lobby is elementless — no element UI here.
 */
export class LobbyScene extends Phaser.Scene {
  private root!: HTMLElement;
  private room!: Room<RoomSchema>;
  private chat: Array<{ from: string; body: string; me: boolean }> = [];
  private chatOpen = true;
  private started = false;

  /**
   * Client-local plaza locomotion (T3) — runs/jumps the LOCAL player's Pon only;
   * never synced / never `room.send` (slice2-protocol §3: lobby avatar is purely
   * cosmetic). Other roster Pons stay statically laid out.
   */
  private platformer!: Platformer;
  private touch: TouchControlsHandle | null = null;
  private emoteWheel: EmoteWheelHandle | null = null;
  /** The local Pon's own DOM node, positioned by the local sim each frame. */
  private localPonEl: HTMLElement | null = null;
  /** True while the Skill/Absorb button is held — channels the suck until it grabs a crate. */
  private absorbHeld = false;
  /** Solid pushable crates (mutable x). `carried` = currently sucked onto the Pon. */
  private crates: Array<{ x: number; carried: boolean }> = [];
  /** The crate currently sucked/connected to the local Pon (slows movement), or null. */
  private carriedCrate: { x: number; carried: boolean } | null = null;
  /** Which side the connected crate sits on: +1 = right, -1 = left. The Pon stays
   *  FACING this way while connected (it's dragging the box), regardless of motion. */
  private carrySign = 1;
  /** Camera scroll offset into the world (px); the world translates by `-camX`. */
  private camX = 0;
  /** Held-direction keyboard state, OR-ed with the touch d-pad each frame. */
  private kbInput = { left: false, right: false };
  private keyHandlers: { down: (e: KeyboardEvent) => void; up: (e: KeyboardEvent) => void } | null =
    null;

  constructor() {
    super('Lobby');
  }

  create(): void {
    const room = net.game;
    if (!room || !session.room) {
      // Defensive: no live room (refresh / direct nav) — back to the browser.
      this.scene.start('RoomBrowser');
      return;
    }
    this.room = room;
    this.started = false;

    this.root = mountRoot(this, this.shellHtml());
    this.bindStaticControls();
    this.setupLocomotion();

    this.room.onStateChange(() => this.render());
    this.room.onMessage<ChatPostedPayload>(ServerMessage.ChatPosted, (m) => {
      this.chat.push({ from: m.from, body: m.body, me: m.senderId === this.room.sessionId });
      if (this.chat.length > 60) this.chat.shift();
      this.renderChat();
    });
    this.room.onMessage<EmotePostedPayload>(ServerMessage.EmotePosted, (m) => this.showEmote(m));
    this.room.onMessage<GameStartingPayload>(ServerMessage.GameStarting, (m) => {
      this.started = true;
      // Slice 3: the host's Start launches the in-stage co-op loop directly. The
      // same GameRoom connection carries the synced physics state.
      this.scene.start('Stage', { mode: m.mode, stageId: m.stageId });
    });
    this.room.onMessage<ErrorPayload>(ServerMessage.Error, (p) => {
      showToast(this.root, this.errorToast(p));
    });
    this.room.onError(() => showToast(this.root, 'Lost connection to the room.'));
    this.room.onLeave(() => {
      if (!this.started) this.scene.start('RoomBrowser');
    });

    if (this.room.state) this.render();
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.teardown());
  }

  private teardown(): void {
    this.room?.removeAllListeners();
    this.touch?.destroy();
    this.touch = null;
    this.emoteWheel?.destroy();
    this.emoteWheel = null;
    if (this.keyHandlers) {
      window.removeEventListener('keydown', this.keyHandlers.down);
      window.removeEventListener('keyup', this.keyHandlers.up);
      this.keyHandlers = null;
    }
  }

  // -- plaza locomotion (T3, client-local — never on the wire) --------------

  /**
   * Stand up the local player's run/jump in the plaza: a client-local
   * `usePlatformer` (port of `docs/design/flow/ui.jsx:322`, plaza params from
   * `screens2.jsx:34-35`), keyboard input (always live), and — on coarse/touch
   * pointers — the shared `◀ ▶` d-pad + Jump (reusing the T2 `mountTouchControls`
   * verbatim, `onJump=doJump`, NO wire send). Other roster Pons stay static.
   */
  private setupLocomotion(): void {
    // Seed the pushable crates (fresh each time the lobby opens).
    this.crates = PLAZA_CRATE_START_X.map((x) => ({ x, carried: false }));
    this.carriedCrate = null;

    this.platformer = new Platformer({
      groundY: PLAZA_GROUND_Y,
      spawn: { x: 200, y: PLAZA_GROUND_Y },
      minX: PLAZA_MIN_X,
      maxX: PLAZA_MAX_X,
      // Mountain blocks are SOLID (block sides + underside — climb them, don't pop
      // up through). Sky platforms are ONE-WAY (jump up through, land on top).
      platforms: [
        ...PLAZA_BLOCKS.map((b) => ({
          x: b.left + b.w / 2,
          y: b.top,
          w: b.w,
          solid: true,
          h: PLAZA_GROUND_Y - b.top + 40,
        })),
        ...PLAZA_FLOATS.map((f) => ({ x: f.x, y: f.y, w: f.w })),
      ],
      bodyHalf: PLAZA_PON_HALF,
      bodyH: 46,
      gravity: 0.9,
      // Lower hop for the cozy plaza — peak ≈ jumpVel² / (2·gravity) ≈ 80px
      // (~2 Pon-heights), down from the old 14.5 (~117px). Tune here.
      jumpVel: 12,
      speed: 2.8,
    });

    // Ignore movement keys while the chat input is focused so typing "a"/"w"/
    // space doesn't drive the Pon (the prototype binds on window; this guard is
    // the one faithful tweak so the lobby chat field still works normally).
    const typing = (): boolean => {
      const el = document.activeElement;
      return el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement;
    };
    const down = (e: KeyboardEvent): void => {
      if (typing()) return;
      const k = e.key.toLowerCase();
      if (k === 'arrowleft' || k === 'a') this.kbInput.left = true;
      else if (k === 'arrowright' || k === 'd') this.kbInput.right = true;
      else if (k === ' ' || k === 'arrowup' || k === 'w') {
        e.preventDefault();
        this.tryJump();
      }
    };
    const up = (e: KeyboardEvent): void => {
      const k = e.key.toLowerCase();
      if (k === 'arrowleft' || k === 'a') this.kbInput.left = false;
      else if (k === 'arrowright' || k === 'd') this.kbInput.right = false;
    };
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    this.keyHandlers = { down, up };

    if (showOnScreenControls()) {
      this.touch = mountTouchControls({
        root: this.root,
        hint: '◀ ▶ run · ⤒ jump',
        onJump: () => this.tryJump(),
        // Absorb: HOLD to channel the suck (gulp animation loops until a crate is
        // grabbed). Once a crate is connected it LATCHES — it stays connected after
        // you release the button, so you can run (slower) while dragging it. You
        // CANNOT jump while carrying (matches the server carry rule). Press the
        // button again to drop it.
        onGrab: () => {
          if (this.carriedCrate) this.dropCarriedCrate(); // tap again = drop
          else this.absorbHeld = true;
        },
        onGrabEnd: () => {
          this.absorbHeld = false; // a caught crate stays latched (don't drop here)
        },
      });
      // Fold the EmoteWheel into the kit's action cluster as the leftmost item so
      // the four controls read as one row (Emote · Jump · Skill), matching the
      // prototype/StageScene — instead of a separate bottom-right button.
      if (this.emoteWheel) {
        // Nudge the emote up + away from the screen edge so it doesn't sit
        // detached at the very bottom of the action row.
        this.emoteWheel.el.style.marginBottom = '12px';
        this.emoteWheel.el.style.marginRight = '2px';
        this.touch.actionCluster.insertBefore(
          this.emoteWheel.el,
          this.touch.actionCluster.firstChild,
        );
      }
    }
  }

  /**
   * Grounded-gated jump edge, blocked while carrying a sucked crate — a carrying
   * Pon CANNOT jump (it only carry-slows), matching the shipped server rule
   * (no-jump-while-carrying, move-only slower). Client-local; no wire change.
   */
  private tryJump(): void {
    if (this.carriedCrate) return;
    this.platformer.jump();
  }

  /**
   * Per-frame: advance the local sim from keyboard | touch input and position
   * the local Pon's DOM node by its feet (the touch jump is a one-frame edge,
   * already applied via `onJump`; clear it). Client-local only — no `room.send`.
   */
  update(_time: number, delta: number): void {
    if (!this.platformer || !this.localPonEl) return;
    const t = this.touch?.state;
    this.platformer.input.left = this.kbInput.left || (t?.left ?? false);
    this.platformer.input.right = this.kbInput.right || (t?.right ?? false);
    this.touch?.clearJumpEdge();
    // Dragging a connected crate slows the run; otherwise full speed.
    this.platformer.speedScale = this.carriedCrate ? PLAZA_CARRY_SLOW : 1;
    const prevY = this.platformer.y;
    this.platformer.step(delta);
    this.landOnCrates(prevY); // stand on top of crates
    this.suckCrate();
    // The connected crate stays rigidly beside the Pon on the ground, on the side
    // it was sucked from — so moving away drags it along. It still can't be dragged
    // through a solid block (clamped).
    if (this.carriedCrate) {
      const prev = this.carriedCrate.x;
      const desired = this.platformer.x + this.carrySign * (PLAZA_PON_HALF + CRATE_SIZE / 2 + 4);
      this.carriedCrate.x = this.clampCrateX(desired, prev);
    }
    this.pushCrates(delta);
    this.positionCrates();
    this.positionLocalPon();
    this.updateCamera();
  }

  /** Disconnect the crate — it stays on the ground wherever it was dragged to. */
  private dropCarriedCrate(): void {
    if (!this.carriedCrate) return;
    this.carriedCrate.carried = false;
    this.carriedCrate = null;
  }

  /** Clamp a crate's world-x so it can't pass through any solid block (the blocks
   *  reach the ground, so a ground crate collides with them like a wall). `from` is
   *  where the crate came from, used to pick which side to stop it on. */
  private clampCrateX(x: number, from: number): number {
    const half = CRATE_SIZE / 2;
    for (const b of PLAZA_BLOCKS) {
      const left = b.left;
      const right = b.left + b.w;
      if (x + half > left && x - half < right) {
        x = from <= (left + right) / 2 ? left - half : right + half;
      }
    }
    return Math.max(PLAZA_MIN_X, Math.min(PLAZA_MAX_X, x));
  }

  /** While Absorb is held and not yet carrying, suck the nearest in-range crate. */
  private suckCrate(): void {
    if (!this.absorbHeld || this.carriedCrate) return;
    const px = this.platformer.x;
    const nearGround = Math.abs(this.platformer.y - PLAZA_GROUND_Y) < CRATE_SIZE;
    if (!nearGround) return;
    for (const c of this.crates) {
      if (!c.carried && Math.abs(c.x - px) < PLAZA_ABSORB_RANGE) {
        c.carried = true;
        this.carriedCrate = c;
        this.carrySign = c.x >= px ? 1 : -1; // lock facing toward the box
        break;
      }
    }
  }

  /** Let the Pon stand on top of a crate: when falling and the feet cross a crate's
   *  top while horizontally over it, plant the feet there (so it's solid + jumpable,
   *  not something you drop straight through). `prevY` is last frame's feet. */
  private landOnCrates(prevY: number): void {
    if (!this.platformer.isFalling()) return;
    const crateTop = PLAZA_CRATE_BASE_Y - CRATE_SIZE;
    const sep = PLAZA_PON_HALF + CRATE_SIZE / 2;
    for (const c of this.crates) {
      if (c.carried) continue;
      if (Math.abs(this.platformer.x - c.x) < sep && prevY <= crateTop + 8 && this.platformer.y >= crateTop) {
        this.platformer.landOn(crateTop);
        break;
      }
    }
  }

  /** Solid-crate horizontal collision: the Pon can't overlap a grounded crate and
   *  shoves it at the slow push speed (carried crates don't collide). Only when the
   *  Pon is on the FLOOR — so standing on a crate or jumping doesn't shove it. */
  private pushCrates(delta: number): void {
    if (Math.abs(this.platformer.y - PLAZA_GROUND_Y) > 6) return; // not on the floor — don't shove
    const dt = Math.min(2.2, delta / 16.67);
    const sep = PLAZA_PON_HALF + CRATE_SIZE / 2;
    const push = PLAZA_PUSH_SPEED * dt;
    for (const c of this.crates) {
      if (c.carried) continue;
      const dx = this.platformer.x - c.x;
      if (Math.abs(dx) >= sep) continue; // not overlapping
      const prev = c.x;
      if (dx <= 0) {
        // Pon on the LEFT pushing right → shove crate right (capped + block-clamped),
        // then clamp the Pon behind it (so shoving into a wall stops the Pon too).
        c.x = this.clampCrateX(Math.min(this.platformer.x + sep, c.x + push), prev);
        this.platformer.setX(c.x - sep);
      } else {
        // Pon on the RIGHT pushing left → shove crate left (capped + block-clamped).
        c.x = this.clampCrateX(Math.max(this.platformer.x - sep, c.x - push), prev);
        this.platformer.setX(c.x + sep);
      }
    }
  }

  /** Place each crate's DOM node on the ground at its world-x (the connected one
   *  is dragged there by the follow step in `update`). */
  private positionCrates(): void {
    for (let i = 0; i < this.crates.length; i++) {
      const node = this.root.querySelector<HTMLElement>(`#lb-crate-${i}`);
      if (!node) continue;
      node.style.left = `${this.crates[i].x}px`;
    }
  }

  /**
   * Place the local Pon's DOM node so its feet sit on the plaza floor at the sim
   * `x` (its `y` is the feet plane). Same feet-on-floor convention as the static
   * roster (`renderPlaza`): the blob SVG body bottom is at viewBox cy≈90/100, so
   * push the box down by the empty `~10%` margin and flip on facing-left.
   */
  private positionLocalPon(): void {
    const el = this.localPonEl;
    if (!el) return;
    const footPad = Math.round(PLAZA_PON_SIZE * 0.1);
    // World-pixel position (the #lb-world layer is PLAZA_WORLD_W wide and panned
    // by the camera), not a % of the viewport.
    el.style.left = `${this.platformer.x}px`;
    el.style.top = `${this.platformer.y}px`;
    el.style.transform = `translate(-50%,calc(-100% + ${footPad + PLAZA_FOOT_SINK}px))`;
    const sprite = el.querySelector<HTMLElement>('.lb-localpon-sprite');
    // While connected to a crate the Pon keeps FACING the crate (it's dragging it),
    // so its facing locks to the connection side regardless of move direction.
    const facingLeft = this.carriedCrate ? this.carrySign < 0 : this.platformer.facing === 'left';
    if (sprite) sprite.style.transform = facingLeft ? 'scaleX(-1)' : '';
    // Drive the designed animation off the local sim state — through the SHARED
    // state map (`ponStateFrom` + `CSS_CLASS_FOR`, `ui/character/ponState.ts`) so
    // the plaza DOM Pon and the in-stage canvas Pon can never drift. The plaza's
    // local platformer state (`idle|walk|jump|fall`) + the crate-latch / channel
    // booleans are funnelled into the unified vocabulary; a crate-latched Pon
    // reads as `carrying` → `.pon-absorbed` (the steady "holding" CSS pose), and
    // channelling reads as `absorbing`. `.pon-fig` is the hook stamped by
    // `ponBlobHtml`.
    const fig = el.querySelector<HTMLElement>('.pon-fig');
    if (fig) {
      const s = this.platformer.ponState;
      const state = ponStateFrom({
        grabbing: this.carriedCrate ? 'crate' : '',
        chainPrev: '',
        chainNext: '',
        channeling: this.absorbHeld,
        moving: s === 'walk',
        grounded: s === 'walk' || s === 'idle',
        vy: s === 'jump' ? -1 : 1,
      });
      const cls = `pon-fig ${CSS_CLASS_FOR[state]}`;
      if (fig.className !== cls) fig.className = cls;
    }
  }

  // -- derived state --------------------------------------------------------

  private roster(): PlayerView[] {
    const out: PlayerView[] = [];
    this.room.state.players.forEach((p) => out.push(p));
    return out;
  }

  private me(): PlayerView | undefined {
    return this.room.state.players.get(this.room.sessionId);
  }

  // -- render ---------------------------------------------------------------

  private shellHtml(): string {
    return `<div class="scene fade-in" style="overflow:hidden">
      <div id="lb-plaza" style="position:absolute;inset:0;z-index:1"></div>

      <!-- top bar -->
      <div style="position:absolute;top:0;left:0;right:0;display:flex;align-items:center;gap:9px;padding:10px 12px;z-index:14">
        <button class="btn ghost icon-btn" id="lb-leave" style="width:40px;height:40px" aria-label="leave">‹</button>
        <div id="lb-name" style="font-weight:700;font-size:16px;color:#3a2a16;text-shadow:0 1px 0 rgba(255,255,255,.4);white-space:nowrap"></div>
        <button id="lb-code" style="cursor:pointer;font-family:var(--display);font-weight:700;font-size:14px;letter-spacing:2px;color:#8a5a10;background:var(--panel);border:2px solid var(--wood);border-radius:10px;padding:4px 9px;box-shadow:0 2px 0 var(--wood)"></button>
        <div style="flex:1"></div>
        <button class="btn pink sm" id="lb-ready" style="min-width:92px"></button>
        <button class="btn gold sm" id="lb-start" style="min-width:108px"></button>
        <div class="chip" id="lb-crew" style="height:34px;gap:6px"></div>
      </div>

      <!-- host mode toggle -->
      <div id="lb-mode" style="position:absolute;left:12px;top:64px;z-index:13;display:flex;align-items:center;gap:5px;background:var(--panel);border:2px solid var(--wood);border-radius:999px;padding:4px 6px;box-shadow:0 2px 0 rgba(124,80,36,.3)"></div>

      <!-- chat -->
      <div id="lb-chatwrap" style="position:absolute;right:12px;top:54px;width:232px;z-index:13;display:flex;flex-direction:column;align-items:flex-end"></div>

      <!-- emotes -->
      <div style="position:absolute;right:14px;bottom:14px;display:flex;gap:8px;z-index:14" id="lb-emotes"></div>
    </div>`;
  }

  private bindStaticControls(): void {
    this.root.querySelector<HTMLButtonElement>('#lb-leave')!.addEventListener('click', () => {
      this.room.send(ClientMessage.Leave);
      void leaveGame();
      forgetRoom(); // explicit Leave → don't auto-rejoin this room next visit
      this.scene.start('RoomBrowser');
    });
    this.root.querySelector<HTMLButtonElement>('#lb-code')!.addEventListener('click', () => {
      const code = session.room?.code ?? '';
      void navigator.clipboard?.writeText(code).catch(() => {});
      showToast(this.root, `Code ${code} copied`);
    });
    this.root.querySelector<HTMLButtonElement>('#lb-ready')!.addEventListener('click', () => {
      const me = this.me();
      this.room.send(ClientMessage.SetReady, { ready: !me?.ready });
    });
    this.root.querySelector<HTMLButtonElement>('#lb-start')!.addEventListener('click', () => {
      this.room.send(ClientMessage.StartGame);
    });

    // Radial co-op comms wheel (T4) — replaces the old 4 tap face-emojis. The 5
    // callouts fire the EXISTING free-form `ClientMessage.Emote` (the wheel hands
    // back `"<icon> <label>!"`); the synced `EmotePosted` bubble renders it via
    // `showEmote`, unchanged.
    const emotes = this.root.querySelector<HTMLElement>('#lb-emotes')!;
    this.emoteWheel = mountEmoteWheel({
      onPick: (callout) => {
        this.room.send(ClientMessage.Emote, { emote: callout });
      },
      // 46px to match the on-screen kit row it gets folded into (`setupLocomotion`).
      size: 46,
    });
    emotes.appendChild(this.emoteWheel.el);
  }

  private render(): void {
    if (!this.room.state) return;
    const state = this.room.state;
    const roster = this.roster();
    const me = this.me();
    const allReady = roster.length > 0 && roster.every((p) => p.ready);
    const readyCount = roster.filter((p) => p.ready).length;

    this.root.querySelector<HTMLElement>('#lb-name')!.textContent = state.name;
    this.root.querySelector<HTMLElement>('#lb-code')!.textContent = state.code;

    const readyBtn = this.root.querySelector<HTMLButtonElement>('#lb-ready')!;
    readyBtn.textContent = me?.ready ? '✓ Ready' : 'Ready up';
    readyBtn.className = me?.ready ? 'btn ghost sm' : 'btn pink sm';

    const startBtn = this.root.querySelector<HTMLButtonElement>('#lb-start')!;
    const isHost = Boolean(me?.host);
    startBtn.style.display = isHost ? '' : 'none';
    startBtn.disabled = !allReady;
    startBtn.textContent = allReady ? 'Start ▶' : `Waiting ${readyCount}/${roster.length}`;

    this.renderCrew(roster, state.max);
    this.renderMode(state.mode, isHost);
    this.renderPlaza(roster);
    this.renderChat();
  }

  private renderCrew(roster: PlayerView[], max: number): void {
    const crew = this.root.querySelector<HTMLElement>('#lb-crew')!;
    crew.innerHTML =
      roster
        .map(
          (p) =>
            `<span title="${escapeHtml(p.name)}" style="position:relative;width:16px;height:16px;border-radius:50%;background:${costumeTint(p.costume)};border:2px solid rgba(94,58,31,.5);opacity:${p.connected ? 1 : 0.4}">${p.ready ? '<span style="position:absolute;right:-4px;top:-6px;font-size:9px;color:var(--leaf-d)">✓</span>' : ''}</span>`
        )
        .join('') +
      `<span style="font-size:11px;color:var(--ink-dim);margin-left:2px">${roster.length}/${max}</span>`;
  }

  private renderMode(mode: GameMode, isHost: boolean): void {
    const wrap = this.root.querySelector<HTMLElement>('#lb-mode')!;
    const pills = [
      { id: 'coop', t: '🤝 Co-op', c: 'var(--teal)' },
      { id: 'pvp', t: '⚔️ PVP', c: 'var(--pink)' },
    ];
    wrap.innerHTML =
      `<span style="font-size:10px;font-weight:700;color:var(--ink-dim);text-transform:uppercase;letter-spacing:.5px;padding:0 3px">Mode</span>` +
      pills
        .map((m) => {
          const on = mode === m.id;
          const cursor = isHost ? 'pointer' : 'default';
          return `<button data-mode="${m.id}" ${isHost ? '' : 'disabled'} style="cursor:${cursor};border:none;border-radius:999px;padding:5px 10px;font-size:12px;font-weight:700;font-family:var(--font);background:${on ? m.c : 'transparent'};color:${on ? '#fff' : 'var(--ink-dim)'};transition:all .15s">${m.t}</button>`;
        })
        .join('');
    if (isHost) {
      wrap.querySelectorAll<HTMLButtonElement>('[data-mode]').forEach((btn) => {
        btn.addEventListener('click', () => {
          this.room.send(ClientMessage.SetMode, { mode: btn.getAttribute('data-mode') as GameMode });
        });
      });
    }
  }

  /**
   * Scrolling plaza world: a parallax background + a `PLAZA_WORLD_W`-wide world
   * layer (ground, trees/decor, the plaza sign, the standing roster, and the
   * local interactive Pon). The world is panned by the follow-camera each frame
   * (`updateCamera`), so the player can roam a map bigger than the frame.
   */
  private renderPlaza(roster: PlayerView[]): void {
    const plaza = this.root.querySelector<HTMLElement>('#lb-plaza')!;
    const groundTop = PLAZA_GROUND_Y;
    // World-kit recipe atoms: the SAME tile/object art every stage consumes
    // (`world/terrain.ts` DOM recipe + `world/props.ts` object pngs).
    const capUrl = grassCapUrl(PLAZA_TILESET);
    const dirtUrl = dirtFillUrl(PLAZA_TILESET);
    const tile = `${DOM_TILE}px`;

    // Trees stand BEHIND the roster (z-index:1 < pons z-index:2), on the flat
    // ground either side of the mountain. Trunk base sinks ~14px so each is rooted.
    const trees: Array<[number, number, string]> = [
      [110, 160, 'Tree_2'], [300, 120, 'Tree_3'], [1280, 150, 'Tree_2'], [1410, 120, 'Tree_3'],
    ];
    const treeImgs = trees
      .map(
        ([x, h, src]) =>
          `<img src="${propUrl(PLAZA_TILESET, `${src}.png`)}" alt="" style="position:absolute;left:${x}px;top:${groundTop}px;height:${h}px;transform:translate(-50%,calc(-100% + 14px));z-index:1;pointer-events:none">`
      )
      .join('');
    // Foreground decor (bushes / mushrooms / stones) on the flat grass.
    const decor: Array<[number, string, number]> = [
      [60, 'Bush_1', 40], [200, 'Mushroom_1', 30], [1110, 'Bush_3', 38], [1240, 'Mushroom_2', 30],
    ];
    const decorImgs = decor
      .map(
        ([x, src, h]) =>
          `<img src="${propUrl(PLAZA_TILESET, `${src}.png`)}" alt="" style="position:absolute;left:${x}px;top:${groundTop}px;height:${h}px;transform:translate(-50%,calc(-100% + 6px));z-index:1;pointer-events:none">`
      )
      .join('');
    // The terraced mountain: connected SOLID blocks, each a grass-top cap (one
    // 2.png row across the top — the SAME tile as the ground) over a dirt-fill body
    // (10.png, the warm textured dirt that matches 2.png's dirt) down to the ground,
    // so the mountain reads as the same earth as the floor, not a different tile.
    // Fill PAST the floor's grass band (~28px of the 128px floor tile) so no green
    // grass sliver peeks below a block's base — the blocks merge into the floor.
    const blockFillY = PLAZA_GROUND_Y + 48;
    const blockImgs = PLAZA_BLOCKS.map((b) => {
      const h = blockFillY - b.top;
      return `<div style="position:absolute;left:${b.left}px;top:${b.top}px;width:${b.w}px;height:${h}px;z-index:1;overflow:hidden;box-shadow:inset -3px 0 6px -3px rgba(0,0,0,.28),inset 3px 0 6px -3px rgba(0,0,0,.12)">
        <div style="position:absolute;inset:0;background:url(${dirtUrl}) left top/${tile} ${tile} repeat"></div>
        <div style="position:absolute;left:0;top:0;width:100%;height:${tile};background:url(${capUrl}) left top/${tile} ${tile} repeat-x"></div>
      </div>`;
    }).join('');
    // Floating one-way platforms: thin grass-capped slabs hovering in the sky
    // (grass-top 2.png + a little 10.png dirt), rounded so they read as floating.
    const floatImgs = PLAZA_FLOATS.map((f) => {
      const left = f.x - f.w / 2;
      return `<div style="position:absolute;left:${left}px;top:${f.y}px;width:${f.w}px;height:44px;z-index:1;overflow:hidden;border-radius:11px 11px 16px 16px;box-shadow:0 8px 13px -5px rgba(0,0,0,.4)">
        <div style="position:absolute;inset:0;background:url(${dirtUrl}) left top/${tile} ${tile} repeat"></div>
        <div style="position:absolute;left:0;top:0;width:100%;height:${tile};background:url(${capUrl}) left top/${tile} ${tile} repeat-x"></div>
      </div>`;
    }).join('');

    // Roster: feet-on-floor, label ABOVE the head. The LOCAL Pon is interactive
    // (its own persistent node positioned by the sim), so it's skipped here and
    // mounted separately; other players stand at fixed world spots near spawn.
    const ponSize = PLAZA_PON_SIZE;
    const footPad = Math.round(ponSize * 0.1);
    const mySessionId = this.room.sessionId;
    const blobs = roster
      .map((p, i) => {
        if (p.sessionId === mySessionId) return '';
        const x = this.staticPonWorldX(i, roster.length);
        return `<div style="position:absolute;left:${x}px;top:${groundTop}px;transform:translate(-50%,calc(-100% + ${footPad + PLAZA_FOOT_SINK}px));display:flex;flex-direction:column;align-items:center;z-index:2">
          <div style="margin-bottom:1px;font-size:11px;font-weight:700;color:#3a2a16;text-shadow:0 1px 0 rgba(255,255,255,.5);white-space:nowrap">${escapeHtml(p.name)}${p.host ? ' 👑' : ''}${p.ready ? ' <span style="color:var(--leaf-d)">✓</span>' : ''}</div>
          ${ponBlobHtml({ colour: costumeTint(p.costume), costume: p.costume, size: ponSize, eyes: 'open', idle: true, faceShift: 6 })}
        </div>`;
      })
      .join('');

    const groundH = PLAZA_H - groundTop + 10;
    plaza.innerHTML = `
      <div id="lb-bg" style="position:absolute;left:0;top:0;width:${PLAZA_WORLD_W}px;height:100%;background:url(${backdropUrl('sunnyland')}) left bottom/auto 100% repeat-x;z-index:0;will-change:transform"></div>
      <div id="lb-world" style="position:absolute;left:0;top:0;width:${PLAZA_WORLD_W}px;height:${PLAZA_H}px;z-index:1;will-change:transform">
        <div style="position:absolute;left:0;top:${groundTop}px;width:${PLAZA_WORLD_W}px;height:${groundH}px;background:url(${capUrl}) left top/${tile} ${tile} repeat-x;box-shadow:inset 0 4px 6px -2px rgba(0,0,0,.12);z-index:0"></div>
        ${treeImgs}
        ${decorImgs}
        ${blockImgs}
        ${floatImgs}
        <div style="position:absolute;left:180px;top:${groundTop - 86}px;text-align:center;z-index:1">
          <div style="background:linear-gradient(180deg,#c79055,#a0703c);border:3px solid #6b4424;border-radius:10px;padding:5px 12px;color:#fff7e6;font-weight:700;font-size:12px;box-shadow:0 4px 0 #5e3a1f">Pon Pon Plaza</div>
          <div style="width:5px;height:56px;background:linear-gradient(90deg,#5e3a1f,#7c4a26,#5e3a1f);margin:0 auto;border-radius:0 0 2px 2px"></div>
        </div>
        ${blobs}
      </div>`;

    const world = plaza.querySelector<HTMLElement>('#lb-world')!;
    // Absorbable crate stack (only the un-eaten ones).
    this.mountCrates(world);
    // (Re)build the LOCAL player's interactive Pon inside the WORLD layer (so it
    // scrolls with the map). `update()` positions it by the sim each frame.
    this.mountLocalPon(world, this.me(), ponSize);
    this.updateCamera(); // seed the scroll so there's no first-frame jump
  }

  /** Inject the pushable crates into the world layer (positioned per frame by
   *  `positionCrates`). z-index:2 so a crate reads in front of the body it leans on. */
  private mountCrates(world: HTMLElement): void {
    world.querySelectorAll('[id^="lb-crate-"]').forEach((n) => n.remove());
    const html = this.crates
      .map(
        (c, i) =>
          `<img id="lb-crate-${i}" src="${propUrl(PLAZA_TILESET, CRATE_FILE)}" alt="" style="position:absolute;left:${c.x}px;top:${PLAZA_CRATE_BASE_Y}px;width:${CRATE_SIZE}px;height:${CRATE_SIZE}px;transform:translate(-50%,-100%);z-index:2;pointer-events:none">`
      )
      .join('');
    world.insertAdjacentHTML('beforeend', html);
  }

  /** World-x (px) where the i-th static roster Pon stands (clustered near spawn). */
  private staticPonWorldX(i: number, len: number): number {
    return PLAZA_VIEW_W * (0.2 + ((i + 1) / (len + 1)) * 0.6);
  }

  /**
   * Cinemachine-style follow camera: pan the world (and parallax background) so
   * the local Pon stays inside the `[PLAZA_CAM_L, PLAZA_CAM_R]` screen dead-zone,
   * clamped to the world bounds. Called each frame after the sim steps.
   */
  private updateCamera(): void {
    if (!this.platformer) return;
    const world = this.root.querySelector<HTMLElement>('#lb-world');
    if (!world) return;
    const screenX = this.platformer.x - this.camX;
    if (screenX < PLAZA_CAM_L) this.camX = this.platformer.x - PLAZA_CAM_L;
    else if (screenX > PLAZA_CAM_R) this.camX = this.platformer.x - PLAZA_CAM_R;
    this.camX = Math.max(0, Math.min(PLAZA_WORLD_W - PLAZA_VIEW_W, this.camX));
    world.style.transform = `translateX(${-this.camX}px)`;
    const bg = this.root.querySelector<HTMLElement>('#lb-bg');
    if (bg) bg.style.transform = `translateX(${-this.camX * PLAZA_BG_PARALLAX}px)`;
  }

  /**
   * Create the local player's own Pon node inside the plaza and capture it into
   * `this.localPonEl`. It is positioned by the sim each frame (`positionLocalPon`),
   * so its CSS `left/top/transform` are seeded then overwritten in `update()`.
   */
  private mountLocalPon(plaza: HTMLElement, me: PlayerView | undefined, ponSize: number): void {
    if (!me) {
      this.localPonEl = null;
      return;
    }
    const el = document.createElement('div');
    el.style.cssText =
      'position:absolute;z-index:2;display:flex;flex-direction:column;align-items:center;will-change:left,top,transform';
    // The blob lives in its own wrapper so facing-left flips ONLY the sprite
    // (`scaleX(-1)`), never the name label (matches the prototype's PonBlob flip).
    el.innerHTML = `<div style="margin-bottom:1px;font-size:11px;font-weight:700;color:#3a2a16;text-shadow:0 1px 0 rgba(255,255,255,.5);white-space:nowrap">${escapeHtml(me.name)}${me.host ? ' 👑' : ''}${me.ready ? ' <span style="color:var(--leaf-d)">✓</span>' : ''}</div>
      <div class="lb-localpon-sprite">${ponBlobHtml({ colour: costumeTint(me.costume), costume: me.costume, size: ponSize, eyes: 'open', faceShift: 6 })}</div>`;
    plaza.appendChild(el);
    this.localPonEl = el;
    this.positionLocalPon();
  }

  private renderChat(): void {
    const wrap = this.root.querySelector<HTMLElement>('#lb-chatwrap');
    if (!wrap) return;
    if (!this.chatOpen) {
      wrap.innerHTML = `<button class="btn ghost" id="lb-chatopen" aria-label="open chat" style="width:46px;height:46px;min-height:0;padding:0;border-radius:50%;font-size:20px;position:relative">💬${this.chat.length ? `<span style="position:absolute;top:-4px;right:-4px;min-width:18px;height:18px;padding:0 4px;border-radius:9px;background:var(--pink);color:#fff;font-size:10px;font-weight:700;display:flex;align-items:center;justify-content:center;border:1.5px solid #fff">${this.chat.length}</span>` : ''}</button>`;
      wrap.querySelector('#lb-chatopen')!.addEventListener('click', () => {
        this.chatOpen = true;
        this.renderChat();
      });
      return;
    }
    const lines = this.chat
      .slice(-12)
      .map(
        (m) =>
          `<div style="font-size:11.5px;line-height:1.3;align-self:${m.me ? 'flex-end' : 'flex-start'};max-width:92%;background:${m.me ? 'var(--teal)' : '#fff7e6'};color:${m.me ? '#143b30' : 'var(--ink)'};${m.me ? '' : 'border:1px solid var(--stroke-soft);'}padding:3px 8px;border-radius:9px">${m.me ? '' : `<b style="color:var(--pink-d)">${escapeHtml(m.from)}: </b>`}${escapeHtml(m.body)}</div>`
      )
      .join('');
    wrap.innerHTML = `<div class="panel" style="padding:8px 10px;width:100%">
      <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:5px">
        <span class="lab" style="margin:0">💬 Chat</span>
        <button id="lb-chatclose" aria-label="collapse chat" style="background:none;border:none;color:var(--ink-faint);cursor:pointer;font-size:13px">✕</button>
      </div>
      <div id="lb-chatlog" class="scroll" style="height:118px;display:flex;flex-direction:column;gap:5px;margin-bottom:6px">${lines}</div>
      <div style="display:flex;gap:5px">
        <input class="field" id="lb-chatinput" style="height:34px;font-size:12.5px" placeholder="Say hi…" maxlength="200" />
        <button class="btn teal sm" id="lb-chatsend" style="min-height:34px">▶</button>
      </div>
    </div>`;
    const log = wrap.querySelector<HTMLElement>('#lb-chatlog')!;
    log.scrollTop = log.scrollHeight;
    wrap.querySelector('#lb-chatclose')!.addEventListener('click', () => {
      this.chatOpen = false;
      this.renderChat();
    });
    const input = wrap.querySelector<HTMLInputElement>('#lb-chatinput')!;
    const send = (): void => {
      const body = input.value.trim();
      if (!body) return;
      this.room.send(ClientMessage.Chat, { body });
      input.value = '';
    };
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') send();
    });
    wrap.querySelector('#lb-chatsend')!.addEventListener('click', send);
  }

  private showEmote(m: EmotePostedPayload): void {
    const roster = this.roster();
    const idx = roster.findIndex((p) => p.sessionId === m.senderId);
    if (idx < 0) return;
    // Bubbles live INSIDE the world layer (world-px), so they scroll with the
    // camera over the sender's head. The LOCAL player tracks its live sim x/y;
    // other (static) players use their fixed world-x.
    const world = this.root.querySelector<HTMLElement>('#lb-world');
    if (!world) return;
    const isLocal = m.senderId === this.room.sessionId && Boolean(this.platformer);
    const x = isLocal ? this.platformer.x : this.staticPonWorldX(idx, roster.length);
    const headTop = (isLocal ? this.platformer.y : PLAZA_GROUND_Y) - PLAZA_PON_SIZE;
    const bubble = document.createElement('div');
    bubble.textContent = m.emote;
    bubble.style.cssText = `position:absolute;left:${x}px;top:${headTop}px;transform:translate(-50%,-100%);z-index:12;background:#fff7e6;border:1.5px solid var(--wood);color:#5a3a16;font-weight:700;border-radius:10px;padding:1px 8px;font-size:16px;pointer-events:none`;
    world.appendChild(bubble);
    window.setTimeout(() => bubble.remove(), 1400);
  }

  private errorToast(p: ErrorPayload): string {
    switch (p.code) {
      case 'not-host':
        return 'Only the host can do that.';
      case 'not-all-ready':
        return 'Everyone must be ready to start.';
      case 'not-enough-players':
        return 'Co-op needs at least 2 players to start.';
      default:
        return p.message || 'Something went wrong.';
    }
  }
}
